import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery using Swiper.js
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [swiperContainer] - The swiper container element.
 * @property {HTMLElement[]} [slides] - The slide elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {any} */
  #swiper = null;
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    
    this.#initializeSwiper();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    if (this.#swiper) {
      this.#swiper.destroy(true, true);
      this.#swiper = null;
    }
    
    this.#controller.abort();
  }

  /**
   * Public: Reinitialize swiper by destroying any existing instance
   * and running the initializer again.
   */
  reinit() {
    if (this.#swiper) {
      try { this.#swiper.destroy(true, true); } catch (_) {}
      this.#swiper = null;
    }
    // Defer to ensure DOM settled
    requestAnimationFrame(() => {
      this.#initializeSwiper();
    });
  }

  /**
   * Initialize Swiper instance
   */
  async #initializeSwiper() {
    try {
      let container = /** @type {HTMLElement|null} */ (this.refs?.swiperContainer || null);
      if (!(container instanceof HTMLElement) || !container.isConnected) {
        container = /** @type {HTMLElement|null} */ (this.querySelector('[ref="swiperContainer"]')) ||
          /** @type {HTMLElement|null} */ (this.querySelector('.media-gallery__swiper'));
      }
      if (!container) return;
      // Swiper expected to be loaded globally via scripts.liquid.
      // Wait briefly (with retries) in case the deferred script has not executed yet.
      const Swiper = await this.#waitForSwiper();
      if (!Swiper) {
        console.warn('[media-gallery] Swiper not available after retries.');
        return;
      }

      // Determine initial slide based on a pre-marked active slide (if any)
      const slidesArray = Array.from(container.querySelectorAll('.swiper-slide'));
      const detectedActiveIndex = slidesArray.findIndex(slide => slide.classList.contains('active'));
      const initialSlide = detectedActiveIndex > -1 ? detectedActiveIndex : 0;

      // Scope navigation and pagination elements to the current container
      const nextEl = container.querySelector('.media-gallery-arrow--next');
      const prevEl = container.querySelector('.media-gallery-arrow--prev');
      const paginationEl = container.querySelector('.swiper-pagination');

      // Configure Swiper options
      const swiperOptions = {
        loop: false,
        spaceBetween: 10,
        slidesPerView: 1,
        initialSlide,
        keyboard: {
          enabled: true,
        },
        a11y: {
          enabled: true,
        },
        breakpoints: {
          768: {
            slidesPerView: 1,
            spaceBetween: 20,
          },
        },
      };

      if (nextEl && prevEl) {
        // @ts-ignore
        swiperOptions.navigation = { nextEl, prevEl };
      }

      if (paginationEl) {
        // @ts-ignore
        swiperOptions.pagination = { el: paginationEl, clickable: true, type: 'bullets' };
      }

      // Initialize Swiper
      this.#swiper = new Swiper(container, swiperOptions);

      // Add custom event listeners
      this.#swiper.on('slideChange', () => {
        this.#onSlideChange();
      });

      // Swiper initialized

    } catch (error) {
      console.warn('Failed to initialize Swiper:', error);
    }
  }

  // Loader method removed; Swiper assets are now bundled as local theme assets.
  /**
   * Wait for window.Swiper to become available.
   * @returns {Promise<any | null>}
   */
  #waitForSwiper(retries = 10, interval = 50) {
    return new Promise((resolve) => {
      /**
       * @param {number} remaining
       */
      const attempt = (remaining) => {
        // @ts-ignore
        const SwiperGlobal = window.Swiper;
        if (SwiperGlobal) return resolve(SwiperGlobal);
        if (remaining <= 0) return resolve(null);
        setTimeout(() => attempt(remaining - 1), interval);
      };
      attempt(retries);
    });
  }

  /**
   * Handle slide change events
   */
  #onSlideChange() {
    if (!this.#swiper) return;
    
    const activeIndex = this.#swiper.activeIndex;
    const event = new CustomEvent('media-gallery:slide-change', {
      detail: { index: activeIndex },
      bubbles: true,
    });
    
    this.dispatchEvent(event);
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    // Destroy existing swiper instance before replacing content
    if (this.#swiper) {
      try {
        this.#swiper.destroy(true, true);
      } catch (_) {}
      this.#swiper = null;
    }

    // Replace inner content of the current custom element to preserve upgrade/lifecycle
    this.innerHTML = newMediaGallery.innerHTML;

    // Ensure any pre-marked active slide becomes the initial slide
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#initializeSwiper();
      });
    });
  };

  /**
   * Navigate to a specific slide
   * @param {number} index - The slide index to navigate to
   */
  slideTo(index) {
    if (this.#swiper && typeof index === 'number') {
      this.#swiper.slideTo(index);
    }
  }

  /**
   * Go to next slide
   */
  slideNext() {
    if (this.#swiper) {
      this.#swiper.slideNext();
    }
  }

  /**
   * Go to previous slide
   */
  slidePrev() {
    if (this.#swiper) {
      this.#swiper.slidePrev();
    }
  }

  /**
   * Get current slide index
   */
  get activeIndex() {
    return this.#swiper ? this.#swiper.activeIndex : 0;
  }

  /**
   * Get total number of slides
   */
  get slidesLength() {
    return this.#swiper ? this.#swiper.slides.length : 0;
  }

  /**
   * Get swiper instance
   */
  get swiper() {
    return this.#swiper;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}